/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ApplicantsService {

    /**
     * Queues a Data Pipeline applicant import
     * Queues up to 100 applicant upserts for the authenticated account. With key_rules, applicant_id (account-wide), kombo_candidate_id and case-insensitive email (within funnel_id) match as a union: one match updates, none creates, several fail. An unknown applicant_id fails. A stored kombo_candidate_id cannot change. Without key_rules, matching uses applicant_id, then funnel-scoped kombo_candidate_id, then email, and an unknown ID can create. Applicant IDs identify only and remain Hire-generated. Poll the status endpoint with the returned job_id.
     * @param xAccessToken API Token
     * @param requestBody
     * @returns any import queued
     * @throws ApiError
     */
    public static postInternalApiWxApplicantsImport(
        xAccessToken: string,
        requestBody?: {
            applicants: Array<{
                /**
                 * Existing applicant ID. With key_rules, an unknown ID fails. Used to identify only — never written.
                 */
                applicant_id?: string;
                /**
                 * External candidate key, matched within the opening. With key_rules, a stored value cannot change; blank input preserves it.
                 */
                kombo_candidate_id?: string;
                /**
                 * Deprecated alias for applicant_id. Used to identify only — never written.
                 */
                external_id?: string;
                /**
                 * Opening external ID. Required when applicant_id does not match an applicant.
                 */
                funnel_id?: string;
                name?: string;
                first_name?: string;
                last_name?: string;
                email?: string;
                phone_number?: string;
                phone_platform?: string;
                time_zone?: string;
                application_locale?: string;
                country_code?: string;
                consent_sms_marketing?: boolean;
                consent_sms_transactional?: boolean;
                consent_calls_marketing?: boolean;
                consent_calls_transactional?: boolean;
                /**
                 * Custom applicant data keyed by data-field key.
                 */
                data?: any;
                address?: {
                    address1?: string;
                    address2?: string;
                    city?: string;
                    state?: string;
                    zip?: string;
                    country?: string;
                };
            }>;
            /**
             * Opt-in, once per applicant. When true, a row that creates a new applicant fires the AI Interview stage invitation for that applicant. Absent or false imports silently.
             */
            invite_on_arrival?: boolean;
            /**
             * Opt into union key matching and immutable stored candidate keys. Absent or false preserves legacy matching.
             */
            key_rules?: boolean;
        },
    ): CancelablePromise<{
        job_id: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/applicants/import',
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Data Pipeline applicant imports are disabled for the account`,
                422: `applicants contains more than 100 rows`,
            },
        });
    }

    /**
     * Gets a Data Pipeline applicant import status
     * Returns processing while the import is queued or running, completed with per-row results after it finishes, or not_found after the one-hour result retention window expires.
     * @param jobId Import job ID returned by the queue endpoint
     * @param xAccessToken API Token
     * @returns any import is processing or completed
     * @throws ApiError
     */
    public static getInternalApiWxApplicantsImportStatus(
        jobId: string,
        xAccessToken: string,
    ): CancelablePromise<({
        status: 'processing';
    } | {
        status: 'completed';
        results: Array<{
            status: 'succeeded' | 'failed';
            index: number;
            external_id?: string;
            was_update?: boolean;
            error?: string;
            /**
             * Stable key-rule reason for a failed keyed row.
             */
            code?: 'multiple' | 'unknownInternal' | 'externalLocked' | 'ineligibleOnly';
            /**
             * Arrival-invitation outcome for a succeeded row. Present only when the request set invite_on_arrival.
             */
            invite?: 'invited' | 'silent' | 'invite_failed';
        }>;
        successCount: number;
        failureCount: number;
        createCount: number;
        updateCount: number;
    })> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/applicants/import/status/{job_id}',
            path: {
                'job_id': jobId,
            },
            headers: {
                'X-ACCESS-TOKEN': xAccessToken,
            },
            errors: {
                403: `Data Pipeline applicant imports are disabled for the account`,
                404: `import job is unknown or expired`,
            },
        });
    }

}